import { analyticsAPI } from './api';

class EngagementTracker {
  constructor() {
    this.heartbeatInterval = null;
    this.isTracking = false;
    this.lastInteraction = Date.now();
    this.isTabVisible = true;
    this.currentVideoId = null;
    this.teamId = null;
    
    // Setup page visibility listener
    this.setupVisibilityListener();
    
    // Setup interaction listeners
    this.setupInteractionListeners();
  }

  setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      this.isTabVisible = !document.hidden;
      
      if (document.hidden) {
        this.sendEvent('blur');
        this.stopHeartbeat();
      } else {
        this.sendEvent('focus');
        // Restart heartbeat if video was playing
        if (this.isTracking && this.currentVideoId) {
          this.startHeartbeat();
        }
      }
    });
  }

  setupInteractionListeners() {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.lastInteraction = Date.now();
      });
    });
  }

  isUserActive() {
    // User is active if last interaction was within 60 seconds
    const IDLE_THRESHOLD = 60000; // 60 seconds
    return Date.now() - this.lastInteraction < IDLE_THRESHOLD;
  }

  async sendEvent(eventType, meta = {}) {
    if (!this.teamId) return;

    try {
      await analyticsAPI.recordEvent({
        team_id: this.teamId,
        video_id: this.currentVideoId,
        event_type: eventType,
        meta: meta
      });
    } catch (error) {
      console.error('Failed to send engagement event:', error);
    }
  }

  startTracking(videoId, teamId) {
    this.currentVideoId = videoId;
    this.teamId = teamId;
    this.isTracking = true;
    this.sendEvent('play', { video_id: videoId });
  }

  stopTracking() {
    this.isTracking = false;
    this.stopHeartbeat();
    if (this.currentVideoId) {
      this.sendEvent('pause', { video_id: this.currentVideoId });
    }
  }

  startHeartbeat() {
    // Only start if not already running
    if (this.heartbeatInterval) return;

    this.heartbeatInterval = setInterval(() => {
      // Only send heartbeat if:
      // 1. Tab is visible
      // 2. User is active (interacted recently)
      // 3. Tracking is enabled
      if (this.isTabVisible && this.isUserActive() && this.isTracking) {
        this.sendEvent('heartbeat', {
          video_id: this.currentVideoId,
          timestamp: Date.now()
        });
      } else if (!this.isUserActive()) {
        // Stop heartbeat if user is idle
        this.stopHeartbeat();
      }
    }, 30000); // Every 30 seconds
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  onSeek(timestamp) {
    this.sendEvent('seek', {
      video_id: this.currentVideoId,
      timestamp: timestamp
    });
  }

  onNote(timestamp) {
    this.sendEvent('note', {
      video_id: this.currentVideoId,
      timestamp: timestamp
    });
  }

  cleanup() {
    this.stopHeartbeat();
    this.isTracking = false;
  }
}

// Singleton instance
const engagementTracker = new EngagementTracker();

export default engagementTracker;
